import { useFormik } from "formik";
import { UserAddress } from "../types/user.interfaces";
import Button from "../components/common/Button";
import Modal from "../components/common/Modal";
import TextField from "../components/common/TextField";
import { addressSchema } from "../utils/validations";
import { useAddAddressMutation, useUpdateAddressMutation } from "../store";
import { toast } from "react-toastify";
import { showToast, ToastType } from "../utils/toast.utils";

interface AddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingAddress: UserAddress | null;
  onSuccess: () => void;
}

const AddressModal = ({
  isOpen,
  onClose,
  editingAddress,
  onSuccess,
}: AddressModalProps) => {
  const [addAddress, { isLoading: isAddingAddress }] = useAddAddressMutation();
  const [updateAddress, { isLoading: isUpdatingAddress }] =
    useUpdateAddressMutation();

  const { handleSubmit, handleChange, handleBlur, values, errors, touched } =
    useFormik({
      initialValues: {
        displayName: editingAddress?.displayName || "",
        street: editingAddress?.street || "",
        zipCode: editingAddress?.zipCode || "",
        city: editingAddress?.city || "",
        country: editingAddress?.country || "",
        isDefault: editingAddress?.isDefault || false,
      },
      validationSchema: addressSchema,
      enableReinitialize: true,
      onSubmit: async (values, { resetForm }) => {
        try {
          if (editingAddress?._id) {
            const result = await updateAddress({
              addressId: editingAddress._id,
              address: values,
            });

            if (!result.error && result.data) {
              showToast("Adresse mise à jour", ToastType.SUCCESS);
              onSuccess();
              resetForm();
            }
          } else {
            const result = await addAddress(values);

            if (!result.error && result.data) {
              showToast("Adresse ajoutée", ToastType.SUCCESS);
              onSuccess();
              resetForm();
            }
          }
        } catch (error) {
          toast.error("Erreur lors de l'enregistrement de l'adresse");
        }
      },
    });

  if (!isOpen) return null;

  return (
    <Modal onClose={onClose}>
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-3 p-4"
      >
        <TextField
          id="displayName"
          placeholder="Nom (ex: Maison)"
          value={values.displayName}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          touched={touched}
        />
        <TextField
          id="street"
          placeholder="Rue"
          value={values.street}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          touched={touched}
        />
        <div className="flex gap-2">
          <TextField
            id="zipCode"
            placeholder="Code Postal"
            value={values.zipCode}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
          />
          <TextField
            id="city"
            placeholder="Ville"
            value={values.city}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
          />
        </div>
        <TextField
          id="country"
          placeholder="Pays"
          value={values.country}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          touched={touched}
        />

        <Button
          type="submit"
          disabled={isAddingAddress || isUpdatingAddress}
          className="mt-4 bg-primary text-white py-2 rounded font-bold justify-center"
        >
          {editingAddress ? "Enregistrer" : "Ajouter"}
        </Button>
      </form>
    </Modal>
  );
};

export default AddressModal;
